import React from "react";
import logo from "../../assets/logo-main-light.svg";
import { Editor } from "@tinymce/tinymce-react";
import {
  DeleteOutlined,
  EditOutlined,
  FormOutlined,
  PaperClipOutlined,
  QuestionCircleOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Col,
  Select,
  Drawer,
  Form,
  Input,
  List,
  Modal,
  Radio,
  Row,
  Space,
  Empty,
  Tooltip,
  Upload,
  message,
} from "antd";

import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  createCampaign,
  updateCampaign,
  readCampaign,
} from "../../redux/actions/campaignsAction";
import { listTags } from "../../redux/actions/contactsAction";
import { getActiveOrganizationName } from "../../utils/organizationId";
import {
  privateGetApi,
  privatePostApi,
  privatePostMultipart,
} from "../../utils/apiHelper";

const MAX_ATTACHMENTS_TOTAL_BYTES = 7 * 1024 * 1024; // SES/SMTP message-size headroom

export default function FormCampaign({ type, formData, fetchRecords }) {
  const dispatch = useDispatch();
  const tags = useSelector((state) => state.contacts.tags);
  const [open, setOpen] = useState(false);
  const editorRef = useRef(null);
  const [form] = Form.useForm();
  const [formDataEdit, setFormDataEdit] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [saveTemplateSaving, setSaveTemplateSaving] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateSubject, setTemplateSubject] = useState("");
  const [templateVisibility, setTemplateVisibility] = useState("private");
  const [pendingFiles, setPendingFiles] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const user = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : {};
  const windowOrigin = window.location.origin;

  const emailSettings = localStorage.getItem("emailSettings")
    ? JSON.parse(localStorage.getItem("emailSettings"))
    : null;

  const isPaidPlan = user.plan && user.plan !== "free";
  // Org slot for the unsubscribe link: contacts are org-scoped and an org
  // can have many member users, so the sending user's own email is the
  // wrong scoping key (routes/root.js's unsubscribe route looks contacts
  // up by organizationId, resolved from this org name -- see
  // organizations.name's unique index, plugins/organizationsCore.js).
  const activeOrganizationName = getActiveOrganizationName() || "";

  const defaultBrandedFooter =
    "<div style='max-width: 600px; margin: auto;'><div class='mceEditable'></div><div class='mceNonEditable'><p style='font-size: 10pt; color: #bfbfbf; text-align: center'>You are receiving this email because you have visited our site or opted for our newsletter. Make sure our messages get to your Inbox (and not your bulk or junk folders).</p><p style='font-size: 10pt; color: #bfbfbf; text-align: center'><a href='" +
    windowOrigin +
    "/unsubscribe/" +
    // Recipient slot: must resolve per-contact, not to the sender's own
    // email -- `_emailAddress_` is substituted with message.contact.emailAddress
    // by mailinflow-mqs's per-contact placeholder pass at send time (see
    // index.js's `_${key}_` substitution loop). Using user.emailAddress
    // here (as this used to) baked the SENDER's email into every
    // recipient's copy, so every unsubscribe click tagged the sender's own
    // contact record instead of the actual recipient's.
    "_emailAddress_" +
    "/" +
    encodeURIComponent(activeOrganizationName) +
    "'>Unsubscribe</a> | <a href='" +
    windowOrigin +
    "/privacy-policy'>Privacy Policy</a></p><p style='font-size: 10pt; color: #bfbfbf; text-align: center'>Powered by<img src='" +
    logo +
    "' style='display: block; margin-top: 5px; margin-left: auto; margin-right: auto;' height='30'/></p></div></div>";

  const storedFooter = emailSettings?.emailFooter;
  const storedWhitelabel = isPaidPlan && emailSettings?.removeWhitelabel === true;

  const defaultFooterTemplate = storedWhitelabel ? storedFooter || "" : defaultBrandedFooter;

  // Templates saved before whitelabel was enabled (or under a different
  // account) may still carry the locked default footer baked into their
  // content. For whitelabel accounts, strip that locked block on load and
  // splice in the org's own footer instead of leaving stale branding stuck
  // in place as a non-editable block.
  const applyWhitelabelFooter = (html) => {
    if (!storedWhitelabel || !html || !html.includes("mceNonEditable")) {
      return html;
    }
    try {
      const doc = new DOMParser().parseFromString(html, "text/html");
      doc.querySelectorAll(".mceNonEditable").forEach((el) => el.remove());
      return doc.body.innerHTML + (storedFooter || "");
    } catch {
      return html;
    }
  };

  const showDrawer = () => {
    setOpen(true);
  };
  const onClose = () => {
    setOpen(false);
  };
  const attachmentsTotalBytes = [
    ...existingAttachments.map((a) => a.size || 0),
    ...pendingFiles.map((f) => f.size || 0),
  ].reduce((sum, size) => sum + size, 0);

  const onSubmit = async (values) => {
    const content = editorRef.current.getContent();

    values.message = content;
    values.status = {
      type: "warning",
      description: "Draft",
    };

    if (attachmentsTotalBytes > MAX_ATTACHMENTS_TOTAL_BYTES) {
      message.error(
        "Total attachment size is too large for email delivery. Keep attachments under ~7MB combined.",
      );
      return;
    }

    setSubmitting(true);
    try {
      let campaignId = type === "edit" ? formData._id : null;
      if (type === "create") {
        const created = await dispatch(createCampaign(values));
        campaignId = created._id;
      }

      const uploadedRefs = [];
      for (const file of pendingFiles) {
        const fd = new FormData();
        fd.append("file", file, file.name);
        const res = await privatePostMultipart(
          `/campaigns/${campaignId}/attachments`,
          fd,
        );
        uploadedRefs.push(res.data.result);
      }

      const attachments = [...existingAttachments, ...uploadedRefs];
      // A fresh campaign is created without attachments (S3 keys are
      // namespaced by campaign id, so uploads can only happen after
      // creation) -- only make the follow-up update call when there's
      // something to attach.
      if (type === "edit" || attachments.length > 0) {
        await dispatch(updateCampaign({ ...values, attachments }, campaignId));
      }

      fetchRecords();
      setOpen(false);
      form.resetFields();
      setPendingFiles([]);
      setExistingAttachments([]);
    } catch (e) {
      console.log(e);
      message.error("Failed to save campaign.");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (open) {
      dispatch(
        listTags({
          pageSize: 10,
          pageNum: 1,
        }),
      ).catch((e) => console.log(e));

      if (type === "edit") {
        dispatch(readCampaign({}, formData._id))
          .then((res) => {
            console.log(res);
            console.log(formData);
            setFormDataEdit(res);
            console.log(formDataEdit);
          })
          .catch((e) => console.log(e));
      } else {
        privateGetApi("/templates", {})
          .then((res) => {
            const result = res?.data?.result || [];
            setTemplates(
              [...result].sort((a, b) =>
                (a.name || "").localeCompare(b.name || ""),
              ),
            );
          })
          .catch(() => setTemplates([]));
      }
    }
  }, [open, type, formData, dispatch, formDataEdit]);

  useEffect(() => {
    if (!open) return;
    setPendingFiles([]);
    setExistingAttachments(
      type === "edit" ? formDataEdit?.attachments || [] : [],
    );
  }, [open, type, formDataEdit]);

  const handleUseTemplate = async (templateId) => {
    if (!templateId || !editorRef.current) return;
    try {
      setTemplateLoading(true);
      const res = await privateGetApi(`/templates/${templateId}`, {});
      const tpl = res?.data?.result;
      if (!tpl) return;
      editorRef.current.setContent(applyWhitelabelFooter(tpl.content || ""));
      if (tpl.subject) {
        form.setFieldValue("subject", tpl.subject);
      }
      message.success("Template loaded.");
    } catch {
      message.error("Failed to load template.");
    } finally {
      setTemplateLoading(false);
    }
  };

  const openSaveTemplate = () => {
    setTemplateName("");
    setTemplateSubject(form.getFieldValue("subject") || "");
    setTemplateVisibility("private");
    setSaveTemplateOpen(true);
  };

  const handleSaveTemplate = async () => {
    const content = editorRef.current?.getContent();
    if (!templateName.trim()) {
      message.error("Enter template name.");
      return;
    }
    if (!content || !content.replace(/<[^>]+>/g, "").trim()) {
      message.error("Template content is empty.");
      return;
    }
    try {
      setSaveTemplateSaving(true);
      await privatePostApi("/templates", {
        name: templateName.trim(),
        subject: templateSubject.trim(),
        content,
        category: "custom",
        visibility: templateVisibility,
      });
      message.success("Template saved.");
      setSaveTemplateOpen(false);
    } catch {
      message.error("Failed to save template.");
    } finally {
      setSaveTemplateSaving(false);
    }
  };

  const templateVisibilityLabel = (t) =>
    t.isSystem ? "System" : t.visibility === "public" ? "Public" : "Private";

  return (
    <>
      {type === "create" ? (
        <Button
          icon={<FormOutlined />}
          title="Add Campaign"
          onClick={showDrawer}
        >
          Quick Campaign
        </Button>
      ) : (
        <Button type="text" onClick={showDrawer} title="Edit Campaign">
          <EditOutlined />
        </Button>
      )}
      <Drawer
        title={type === "create" ? "New Campaign" : "Edit Campaign"}
        width="min(720px, 90vw)"
        placement="right"
        maskClosable={false}
        destroyOnClose={true}
        onClose={onClose}
        open={open}
        extra={
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button onClick={openSaveTemplate}>Save as Template</Button>
            <Button type="primary" loading={submitting} onClick={form.submit}>
              Save
            </Button>
          </Space>
        }
      >
        {!formDataEdit && type === "edit" ? (
          <Empty description="There is no data to display." />
        ) : (
          <Form
            form={form}
            layout="vertical"
            initialValues={
              type === "edit" ? { ...formDataEdit } : { ...formData }
            }
            onFinish={onSubmit}
          >
            <Row gutter={[16, 16]}>
              {type === "create" && (
                <Col span={24}>
                  <Form.Item label="Start from a template">
                    <Select
                      allowClear
                      placeholder="Choose a template, or leave blank to start from scratch"
                      loading={templateLoading}
                      onChange={handleUseTemplate}
                      options={templates.map((t) => ({
                        value: t.id,
                        label: `${t.name} (${templateVisibilityLabel(t)})`,
                      }))}
                    />
                  </Form.Item>
                </Col>
              )}
              <Col span={24}>
                <Form.Item
                  name="subject"
                  label="Subject"
                  rules={[
                    {
                      required: true,
                      message: "Please enter subject line",
                    },
                  ]}
                >
                  <Input placeholder="Please enter subject line" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="tags"
                  label="Tags"
                  help="Campaign will be sent to all contacts if no tags are specified."
                >
                  <Select
                    mode="tags"
                    placeholder="Please enter contact tags"
                    options={tags.map((tag) => ({
                      value: tag,
                      label: tag,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="excludeTags"
                  label="Exclude tags"
                  initialValue={["unsubscribed"]}
                  help="Contacts with any of these tags will never receive this campaign."
                >
                  <Select
                    mode="tags"
                    placeholder="Please enter tags to exclude"
                    options={tags.map((tag) => ({
                      value: tag,
                      label: tag,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Attachments"
                  extra="Sent to every recipient of this campaign."
                >
                  <Upload
                    beforeUpload={(file) => {
                      setPendingFiles((prev) => [...prev, file]);
                      return false;
                    }}
                    showUploadList={false}
                  >
                    <Button icon={<UploadOutlined />}>Add attachment</Button>
                  </Upload>
                  {(existingAttachments.length > 0 || pendingFiles.length > 0) && (
                    <List
                      size="small"
                      style={{ marginTop: 8 }}
                      dataSource={[
                        ...existingAttachments.map((a, i) => ({
                          key: `existing-${a.key}`,
                          label: a.filename,
                          onRemove: () =>
                            setExistingAttachments((prev) =>
                              prev.filter((_, idx) => idx !== i),
                            ),
                        })),
                        ...pendingFiles.map((f, i) => ({
                          key: `pending-${f.uid || i}`,
                          label: f.name,
                          onRemove: () =>
                            setPendingFiles((prev) =>
                              prev.filter((_, idx) => idx !== i),
                            ),
                        })),
                      ]}
                      renderItem={(item) => (
                        <List.Item
                          actions={[
                            <Button
                              key="remove"
                              type="text"
                              danger
                              size="small"
                              icon={<DeleteOutlined />}
                              onClick={item.onRemove}
                            />,
                          ]}
                        >
                          <PaperClipOutlined style={{ marginRight: 8 }} />
                          {item.label}
                        </List.Item>
                      )}
                    />
                  )}
                </Form.Item>
                <Form.Item
                  name="includeContactAttachments"
                  valuePropName="checked"
                  initialValue={false}
                  style={{ marginTop: -8 }}
                >
                  <Checkbox>
                    Also send each contact's own attachment, if set{" "}
                    <Tooltip title={
                      <>
                        Set per-contact on the{" "}
                        <Link to="/app/contacts" target="_blank">
                          Contacts page
                        </Link>{" "}
                        (edit a contact, or bulk-match many files by email at
                        once). Recipients with both get both.
                      </>
                    }>
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </Checkbox>
                </Form.Item>
              </Col>
              <Col span={24}>
                <br />
                <Editor
                  tinymceScriptSrc={"/tinymce/tinymce.min.js"}
                  onInit={(evt, editor) => {
                    editorRef.current = editor;
                    if (!formDataEdit && !formData?.message) {
                      editor.setContent(defaultFooterTemplate);
                    }
                  }}
                  initialValue={
                    formDataEdit ? formDataEdit.message : defaultFooterTemplate
                  }
                  init={{
                    // Render TinyMCE with a light/white editor theme.
                    skin: "oxide",
                    content_css: "default",
                    content_style: `
                      body.mce-content-body {
                        background-color: #ffffff !important;
                        color: #111827 !important;
                      }
                      body.mce-content-body a {
                        color: #2563eb !important;
                      }
                    `,
                    setup: (editor) => {
                      const contactPlaceholders = [
                        { label: "First Name", token: "_firstName_" },
                        { label: "Last Name", token: "_lastName_" },
                        { label: "Email Address", token: "_emailAddress_" },
                        { label: "Phone Number", token: "_phoneNumber_" },
                      ];

                      // TinyMCE toolbar icons (incl. splitbuttons) are sized by CSS.
                      // Inject a scoped override to make the "Contact fields" icon smaller.
                      const cssId = "mailinflow-tinymce-toolbar-icon-size";
                      if (!document.getElementById(cssId)) {
                        const style = document.createElement("style");
                        style.id = cssId;
                        style.innerHTML = `
                          /* toolbar button icons */
                          .tox .tox-tbtn__icon svg {
                            width: 14px !important;
                            height: 14px !important;
                          }
                          .tox .tox-tbtn__icon {
                            width: 14px !important;
                            height: 14px !important;
                          }
                        `;
                        document.head.appendChild(style);
                      }

                      editor.ui.registry.addSplitButton("contactFields", {
                        icon: "user",
                        tooltip:
                          "Contact fields (will be replaced per recipient)",
                        onAction: () => {
                          // When user clicks main button portion, insert first placeholder.
                          editor.insertContent(contactPlaceholders[0].token);
                        },
                        fetch: (success) => {
                          success(
                            contactPlaceholders.map(({ label, token }) => ({
                              type: "choiceitem",
                              text: label,
                              value: token,
                            })),
                          );
                        },
                        onItemAction: (_api, value) => {
                          // `value` is the placeholder token.
                          editor.insertContent(value);
                        },
                      });
                    },
                    branding: false,
                    height: "calc(100vh - 120px)",
                    menubar: false,
                    selector: "textarea",
                    editable_root: false,
                    editable_class: "mceEditable",
                    convert_urls: true,
                    relative_urls: false,
                    remove_script_host: false,
                    /* enable title field in the Image dialog*/
                    image_title: true,
                    /* enable automatic uploads of images represented by blob or data URIs*/
                    automatic_uploads: true,
                    /*
                    URL of our upload handler (for more details check: https://www.tiny.cloud/docs/configure/file-image-upload/#images_upload_url)
                    images_upload_url: 'postAcceptor.php',
                    here we add custom filepicker only to Image dialog
                    */
                    file_picker_types: "image",
                    /* and here's our custom image picker*/
                    file_picker_callback: (cb, value, meta) => {
                      const input = document.createElement("input");
                      input.setAttribute("type", "file");
                      input.setAttribute("accept", "image/*");

                      input.addEventListener("change", (e) => {
                        const file = e.target.files[0];

                        const reader = new FileReader();
                        reader.addEventListener("load", () => {
                          /*
                          Note: Now we need to register the blob in TinyMCEs image blob
                          registry. In the next release this part hopefully won't be
                          necessary, as we are looking to handle it internally.
                        */
                          const id = "blobid" + new Date().getTime();
                          /* global tinymce */
                          const blobCache =
                            tinymce.activeEditor.editorUpload.blobCache;
                          const base64 = reader.result.split(",")[1];
                          const blobInfo = blobCache.create(id, file, base64);
                          blobCache.add(blobInfo);

                          /* call the callback and populate the Title field with the file name */
                          cb(blobInfo.blobUri(), { title: file.name });
                        });
                        reader.readAsDataURL(file);
                      });

                      input.click();
                    },
                    plugins: [
                      "advlist",
                      "autolink",
                      "lists",
                      "link",
                      "image",
                      "charmap",
                      "preview",
                      "insertdatetime",
                      "anchor",
                      "searchreplace",
                      "visualblocks",
                      "code",
                      "media",
                      "table",
                      "wordcount",
                      "quickbars",
                      "emoticons",
                      "fullscreen",
                      "importcss",
                      "autosave",
                      "save",
                      "directionality",
                      "visualchars",
                      "codesample",
                      "pagebreak",
                      "nonbreaking",
                      "help",
                      "accordion",
                    ],
                    toolbar: [
                      "undo redo | fontfamily fontsize | bold italic underline | forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist | outdent indent | strikethrough subscript superscript | blockquote removeformat",
                      "link emoticons charmap image media table codesample | contactFields code preview fullscreen",
                    ],
                    quickbars_insert_toolbar:
                      "image media table charmap emoticons blockquote",
                    quickbars_selection_toolbar:
                      "bold italic underline strikethrough subscript superscript forecolor backcolor | link removeformat",
                  }}
                />
              </Col>
            </Row>
          </Form>
        )}
      </Drawer>
      <Modal
        title="Save as Template"
        open={saveTemplateOpen}
        onCancel={() => setSaveTemplateOpen(false)}
        onOk={handleSaveTemplate}
        okText="Save Template"
        confirmLoading={saveTemplateSaving}
      >
        <Form layout="vertical">
          <Form.Item label="Template Name" required>
            <Input
              placeholder="Enter template name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
            />
          </Form.Item>
          <Form.Item label="Template Subject (optional)">
            <Input
              placeholder="Optional subject line"
              value={templateSubject}
              onChange={(e) => setTemplateSubject(e.target.value)}
            />
          </Form.Item>
          <Form.Item label="Visibility">
            <Radio.Group
              value={templateVisibility}
              onChange={(e) => setTemplateVisibility(e.target.value)}
            >
              <Radio value="private">Private (only your org)</Radio>
              <Radio value="public">Public (shared with all orgs)</Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
